/// <reference path='userDefinedControls.module.ts' />
/// <reference path='../../../../typings/index.d.ts' />

module Meevo.Plugins.UserDefinedControls {

    import ControlTypeEnum = Meevo.Model.ControlTypeEnum;
    import AppAreaEnum = Meevo.Model.AppAreaEnum;
    import ObjectStateEnum = Meevo.Model.ObjectStateEnum;
    import IUserDefinedControl = Meevo.Model.IUserDefinedControl;
    import IAppAreaTab = Meevo.Model.IAppAreaTab;

    export interface IUserDefinedControlsService {
        controlTypes: Object,
        controlLocations: Object,
        controls: Array<IUserDefinedControl>,
        appAreaTabs: Array<IAppAreaTab>,
        search: (ISearchCriteria) => ng.IPromise<any>,
        searchTabs: (ISearchCriteria) => ng.IPromise<any>
    }

    export interface IBaseSearchCriteria {
        objectStateEnums: Array<ObjectStateEnum>
        appAreaEnum?: AppAreaEnum
    }

    export interface ISearchControlCriteria extends IBaseSearchCriteria {
        shortcut: string,
        controlTypeEnum?: ControlTypeEnum
    }

    export interface ISearchTabCriteria extends IBaseSearchCriteria {
        filter: string
    }

    export class UserDefinedControlsService implements IUserDefinedControlsService {
        private _controlTypes: Object = {};
        private _controlLocations: Object = {};
        private _controls: Array<IUserDefinedControl> = [];
        private _appAreaTabs: Array<IAppAreaTab> = [];
        private _typesList: Array<string> = ['DropDown', 'Radio', 'ListBox', 'CheckBox', 'TextArea', 'TextBox', 'DatePicker', 'ImageUpload', 'DocumentUpload'];
        private _locationsList: Array<string> = ['None', 'Employee', 'Client', 'Product', 'Service', 'Booking', 'Checkin', 'Checkout'];

        constructor (
            protected dataService: Meevo.Components.BaseClasses.IDataService
        ) {
            UserDefinedControlsService.parseEnumToObject(ControlTypeEnum, this.controlTypes, this._typesList, 'controlTypes');
            UserDefinedControlsService.parseEnumToObject(AppAreaEnum, this.controlLocations, this._locationsList, 'controlLocations');
        }

        /**
         * Search custom controls by criteria
         * @param {ISearchCriteria} criteria
         * @returns {ng.IPromise} promise
         * */
        public search(criteria: ISearchControlCriteria): ng.IPromise<any> {

            return this.dataService.getListPaged('CustomData/Control', {
                pageNumber: 0,
                itemsPerPage: 100000,
                sortBy: 'ControlName',
                sortDirection: Meevo.Model.SortDirection.Ascending,
                criteria
            })
                .then((data: Array<IUserDefinedControl>|any) => {
                    this.controls = data;
                });
        }

        /**
         * Search app area tabs by criteria
         * @param {ISearchTabCriteria} criteria
         * @returns {ng.IPromise} promise
         * */
        public searchTabs(criteria: ISearchTabCriteria): ng.IPromise<any> {
            return this.dataService.getListPaged('CustomData/TabView', {
                pageNumber: 0,
                itemsPerPage: 100000,
                sortDirection: Meevo.Model.SortDirection.Ascending,
                criteria
            })
                .then((data: Array<IAppAreaTab>|any) => {
                    this.appAreaTabs = data;
                });
        }

        /**
         * Parse enum to object with keys - enum values, and values - enum keys to display on drop down
         * @param {any} source - enum
         * @param {Object} target - destination
         * @param {Array<string>} propsOrder - properties order
         * @param {string} type - enum type
         * */
        static parseEnumToObject(source: any, target: Object, propsOrder, type?: string): void {
            let key: any;

            for (let i = 0; i < propsOrder.length; i += 1) {
                let name;

                name = propsOrder[i];
                key = Number(source[name]);

                if (type === 'controlTypes') {
                    target[i] = {
                        number: key,
                        name: UserDefinedControlsService.getControlTypeName(key)
                    };
                } else if (type === 'controlLocations') {
                    target[i] = {
                        number: key,
                        name: UserDefinedControlsService.getControlLocationName(key)
                    };
                } else {
                    target[i] = {
                        number: key,
                        name
                    };
                }
            }
        }

        /**
         * Get formatted control class name from enum value
         * @param {ControlTypeEnum} value - control class value
         * */
        static getControlTypeName (value: ControlTypeEnum): string {
            let name = '';

            switch (value) {
                case ControlTypeEnum.Markup:
                    name = 'Markup';
                    break;
                case ControlTypeEnum.DropDown:
                    name = 'Drop Down';
                    break;
                case ControlTypeEnum.Radio:
                    name = 'Radio Button';
                    break;
                case ControlTypeEnum.ListBox:
                    name = 'List Box';
                    break;
                case ControlTypeEnum.CheckBox:
                    name = 'Check Box';
                    break;
                case ControlTypeEnum.TextArea:
                    name = 'Text Area';
                    break;
                case ControlTypeEnum.TextBox:
                    name = 'Text Box';
                    break;
                case ControlTypeEnum.AlertingDatePicker:
                    name = 'Alerting Date Picker';
                    break;
                case ControlTypeEnum.DatePicker:
                    name = 'Date';
                    break;
                case ControlTypeEnum.ImageUpload:
                    name = 'Image Upload';
                    break;
                case ControlTypeEnum.DocumentUpload:
                    name = 'Document Upload';
                    break;
                case ControlTypeEnum.SingleSelectList:
                    name = 'Single Select List';
                    break;
                case ControlTypeEnum.MultiSelectList:
                    name = 'Multi Select List';
                    break;
                case ControlTypeEnum.Signature:
                    name = 'Signature';
                    break;
                case ControlTypeEnum.Initials:
                    name = 'Initials';
                    break;
            }

            return name;
        }

        /**
         * Get formatted control location name from enum value
         * @param {AppAreaEnum} value - control class value
         * */
        static getControlLocationName (value: AppAreaEnum): string {
            let name = '';

            switch (value) {
                case AppAreaEnum.Booking:
                    name = 'Booking';
                    break;
                case AppAreaEnum.Checkin:
                    name = 'Checkin';
                    break;
                case AppAreaEnum.Checkout:
                    name = 'Checkout';
                    break;
                case AppAreaEnum.Client:
                    name = 'Clients';
                    break;
                case AppAreaEnum.Employee:
                    name = 'Employees';
                    break;
                case AppAreaEnum.Product:
                    name = 'Products';
                    break;
                case AppAreaEnum.Service:
                    name = 'Services';
                    break;
                case AppAreaEnum.None:
                    name = 'None';
                    break;
            }

            return name;
        }

        /**
         * Give the ability to sort items in array
         * @param {Array<any>} source - array of items
         * @param {Number} oldIndex - which item should we move
         * @param {Number} newIndex - where should we move the item
         * */
        static moveItem(source: Array<any>, oldIndex: number, newIndex: number): void {
            if (newIndex >= source.length) {
                let k = newIndex - source.length;
                while ((k--) + 1) {
                    source.push(undefined);
                }
            }
            source.splice(newIndex, 0, source.splice(oldIndex, 1)[0]);
        }

        get controlTypes (): Object {
            return this._controlTypes;
        }

        get controlLocations(): Object {
            return this._controlLocations;
        }

        get controls(): Array<IUserDefinedControl> {
            return this._controls;
        }

        get appAreaTabs(): Array<IAppAreaTab> {
            return this._appAreaTabs;
        }

        set controls(list: Array<IUserDefinedControl>) {
            let uniqueControlTIds = {};

            list.forEach((item) => {
                let controlTId = item.control_TId.toString();

                if (uniqueControlTIds[controlTId]) {
                    if (uniqueControlTIds[controlTId].controlServiceName !== item.controlServiceName) {
                        uniqueControlTIds[controlTId].controlServiceName = 'Multiple Services';
                    }

                    if (uniqueControlTIds[controlTId].businessTypeName !== item.businessTypeName) {
                        uniqueControlTIds[controlTId].businessTypeName = 'Multiple Business Types';
                    }
                } else {
                    uniqueControlTIds[controlTId] = item;
                }
            });

            list = Object.keys(uniqueControlTIds).map((key) => {
                return uniqueControlTIds[key];
            });

            list.map((item) => {
                let tabName = item.tabName ? item.tabName : 'Unassigned',
                    serviceName = item.controlServiceName ? item.controlServiceName : 'Unassigned';

                item['parsedClassName'] = UserDefinedControlsService.getControlTypeName(item.controlTypeEnum);
                item['parsedLocation'] = AppAreaEnum[item.appAreaEnum];

                // parse location and tab
                switch (item.appAreaEnum) {
                    case AppAreaEnum.None:
                        item['parsedLocationTab'] = 'Unassigned';
                        break;
                    case AppAreaEnum.Booking:
                    case AppAreaEnum.Checkin:
                    case AppAreaEnum.Checkout:
                        item['parsedLocationTab'] = serviceName;
                        break;
                    default:
                        item['parsedLocationTab'] = tabName;
                }

                // parse business type
                switch (item.appAreaEnum) {
                    case AppAreaEnum.Booking:
                    case AppAreaEnum.Checkin:
                    case AppAreaEnum.Checkout:
                    case AppAreaEnum.Client:
                    case AppAreaEnum.Employee:
                        item['parsedBusinessType'] = item.businessTypeName ? item.businessTypeName : 'All Business Types';
                        break;
                    default:
                        item['parsedBusinessType'] = item.businessTypeName ? item.businessTypeName : 'Unassigned';
                }

                item['parsedDisableSelect'] = item.appAreaEnum !== AppAreaEnum.None && (item.tabName || item.controlServiceName);

                item['parsedMultipleServices'] = item.controlServiceName !== 'Multiple Services';
            });

            this._controls = list;
        }

        set appAreaTabs(list: Array<IAppAreaTab>) {
            list.map((item) => {
                item['parsedLocation'] = AppAreaEnum[item.appAreaEnum];

                // parse business type
                switch (item.appAreaEnum) {
                    case AppAreaEnum.Booking:
                    case AppAreaEnum.Checkin:
                    case AppAreaEnum.Checkout:
                    case AppAreaEnum.Client:
                    case AppAreaEnum.Employee:
                        item['parsedBusinessType'] = item.businessTypeName ? item.businessTypeName : 'All Business Types';
                        break;
                    default:
                        item['parsedBusinessType'] = item.businessTypeName ? item.businessTypeName : 'Unassigned';
                }

            });

            this._appAreaTabs = list;
        }

        static factory(): Function {
            let instance = (
                dataService: Meevo.Components.BaseClasses.IDataService
            ) => {
                return new UserDefinedControlsService(dataService);
            };

            instance.$inject = ['meevo.components.common.DataService'];
            return instance;
        }
    }

    angular.module('meevo.plugins.userDefinedControls')
        .service('meevo.plugins.userDefinedControls.userDefinedControlsService', UserDefinedControlsService.factory());
}
